import React, { useEffect, useState } from "react"

import { graphql, Link } from "gatsby"
import { DatoCmsSlugsConfiguration, HeaderFieldsFragment, DatoCmsAllLocalesForDatoCmsArticleSlug } from "../../../../graphql-types"

import * as config from "../../../../config.js"
import * as styles from "./header.module.scss"
import { elementOffset } from "../../common/functions/functions"
import SearchModule from "../../common/jsx/search-module/search-module"
import { useLocale } from "../../../context/locale-context"
import useSlugValue from "../../../hooks/use-slug-value"
import useWindowWidth from "../../../hooks/use-window-width"
import BurgerMenu from "./burger-menu/burger-menu"
import { changeLanguage } from "../../common/functions/functions"

type HeaderProps = {
  data: HeaderFieldsFragment
  slugs: DatoCmsSlugsConfiguration
  currentRoute?: string
  allSlugLocales: [DatoCmsAllLocalesForDatoCmsArticleSlug]
}

const Header: React.FC<HeaderProps> = ({ data, slugs, currentRoute, allSlugLocales}) => {
  const [offset, setOffset] = useState(0)
  const { locale, defaultLocale } = useLocale()
  const isMobile = useWindowWidth() <= 825
  const isOnBrowser = typeof window !== "undefined"

  const contactSlug = useSlugValue(slugs._allContactPageSlugLocales, locale)
  const pniSlug = useSlugValue(slugs._allProjectsNewsInsightsPageSlugLocales, locale)
  const searchRequestSlug = locale === defaultLocale ? `${pniSlug}` : `${locale}/${pniSlug}`

  const displayLinks = (): JSX.Element => (
    <ul className={styles.linksContainer}>
      {data.v2Menu?.map((menuLinks, index) => (
        <li key={index} className={styles.linkElementContainer}>
          {menuLinks.isAnchorInPage ? (
            <span
              onClick={() => {
                const anchor = document.getElementById(menuLinks.anchorSection)
                if (anchor) {
                  anchor.scrollIntoView({ behavior: "smooth" })
                }
              }}
              className={styles.linkElement}
            >
              {menuLinks.title}
            </span>
          ) : (
            <Link
              to={
                menuLinks.isInternalPage
                  ? locale === defaultLocale
                  ? `/${menuLinks.internalLink.slug}`
                  : `/${locale}/${menuLinks.internalLink.slug}`
                : menuLinks.externalLink
            }
            target={!menuLinks.isInternalPage && "_blank"}
            className={styles.linkElement}
          >
              {menuLinks.title}
            </Link>
          )}
        </li>
      ))}
    </ul>
  )

  const displaySearchAndLang = (): JSX.Element => (
    <div
      className={styles.websiteSettingsAndSearchCollapsed}
    >
      <SearchModule
        searchPlaceholder={data.searchPlaceholder}
        searchRequestSlug={searchRequestSlug}
        buttonSearchAriaLabel={data.buttonSearchAriaLabel}
      />
      <ul className={styles.langSelectorCollapsed}>
        <li>
          <Link to={changeLanguage(currentRoute, slugs, allSlugLocales, "en", defaultLocale)} className={styles.lang} title="EN English version">
            <span style={{ fontWeight: locale === "en" ? "bold" : "normal" }}>EN</span>
          </Link>
        </li>
        <li>
          /
        </li>
        <li>
          <Link to={changeLanguage(currentRoute, slugs, allSlugLocales, "fr", defaultLocale)} className={styles.lang} title="FR version française">
            <span style={{ fontWeight: locale === "fr" ? "bold" : "normal" }}>FR</span>
          </Link>
        </li>
      </ul>
    </div>
  )

  useEffect(() => {
    const handler = () => setOffset(window.pageYOffset)
    window.addEventListener("scroll", handler)
    return () => window.removeEventListener("scroll", handler)
  }, [])

  if (isMobile) {
    return (
      <header className={styles.headerMobile} style={{ height: 80 }}>
        <BurgerMenu
          data={data}
          contactSlug={contactSlug}
          slugs={slugs}
          searchRequestSlug={searchRequestSlug}
          allSlugLocales={allSlugLocales}
          currentRoute={currentRoute}
        />
      </header>
    )
  } else {
    return (
      <header className={styles.headerCollapsed} role="banner">
        <div className={styles.headerContent}>
          <section className={styles.logoContainer}>
            <Link to={defaultLocale === locale ? "/" : `/${locale}`}>
              <img src={data.v2Logo.url} alt={data.v2Logo.alt || ""} className={styles.logo} />
            </Link>
          </section>
          <section className={styles.websiteContentCollapsed}>
            {displayLinks()}
            {displaySearchAndLang()}
          </section>
        </div>
      </header>
    )
  }
}

export const enableDidomi = () => {
  const head = document.getElementsByTagName("head")
  const script = document.createElement("script")

  script.type = "text/javascript"
  script.text =
    `window.gdprAppliesGlobally=true;(function(){function a(e){if(!window.frames[e]){if(document.body&&document.body.firstChild){var t=document.body;var n=document.createElement("iframe");n.style.display="none";n.name=e;n.title=e;t.insertBefore(n,t.firstChild)}\n` +
    `else{setTimeout(function(){a(e)},5)}}}function e(n,r,o,c,s){function e(e,t,n,a){if(typeof n!=="function"){return}if(!window[r]){window[r]=[]}var i=false;if(s){i=s(e,t,n)}if(!i){window[r].push({command:e,parameter:t,callback:n,version:a})}}e.stub=true;function t(a){if(!window[n]||window[n].stub!==true){return}if(!a.data){return}\n` +
    `var i=typeof a.data==="string";var e;try{e=i?JSON.parse(a.data):a.data}catch(t){return}if(e[o]){var r=e[o];window[n](r.command,r.parameter,function(e,t){var n={};n[c]={returnValue:e,success:t,callId:r.callId};a.source.postMessage(i?JSON.stringify(n):n,"*")},r.version)}}\n` +
    `if(typeof window[n]!=="function"){window[n]=e;if(window.addEventListener){window.addEventListener("message",t,false)}else{window.attachEvent("onmessage",t)}}}e("__tcfapi","__tcfapiBuffer","__tcfapiCall","__tcfapiReturn");a("__tcfapiLocator");(function(e){\n` +
    `var t=document.createElement("script");t.id="spcloader";t.type="text/javascript";t.async=true;t.src="https://sdk.privacy-center.org/"+e+"/loader.js?target="+document.location.hostname;t.charset="utf-8";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n)})("${config.didomi_api_key}")})();`

  head[0].prepend(script)
}

export const enableGoogleAnalytics = () => {
  return (
    <script
      type="didomi/javascript"
      data-vendor="c:googleana-4TXnJigR"
      dangerouslySetInnerHTML={{
        __html: `(function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
            (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
            m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
            })(window,document,'script','https://www.google-analytics.com/analytics.js','ga');
            ga('create', '${config.google_analytics_key}', 'auto');
            ga('send', 'pageview');`,
      }}
    />
  )
}

export const enableGoogleTagManagerHead = () => {
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${config.google_tag_manager_id}');`,
      }}
    />
  )
}

export const enableGoogleTagManagerBody = () => {
  const body = document.getElementsByTagName("body")
  const noscript = document.createElement("noscript")
  const iframe = document.createElement("iframe")

  iframe.src = `https://www.googletagmanager.com/ns.html?id=${config.google_tag_manager_id}`
  iframe.height = "0"
  iframe.width = "0"
  iframe.setAttribute("style", "display:none;visibility:hidden")

  noscript.appendChild(iframe)

  body[0].prepend(noscript)
}

export const fragment = graphql`
  fragment HeaderFields on DatoCmsHeader {

    v2Logo {
      alt
      format
      title
      url
    }
    v2Menu {
      title
      isInternalPage
      internalLink {
        slug
      }
      externalLink
      isAnchorInPage
      anchorSection
    }
    searchPlaceholder
    buttonSearchAriaLabel
    languageSelectorAltLabel
  }
`

export default Header
